import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import AddressPickerMap from '../components/AddressPickerMap';
import AddressDetailsForm from '../components/AddressDetailsForm';
import { getUserHomeRoute, needsProfileCompletion, hasValidDeliveryAddress } from '../utils/userUtils';
import { db } from '../firebase/firebaseConfig';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import './AddressSetupPage.css';

/**
 * Address Setup Page
 * Helps users set their delivery address after login
 */
const AddressSetupPage = () => {
  const { user, userType, userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [addressSelected, setAddressSelected] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [step, setStep] = useState('map'); // 'map' or 'details'

  // Profile completion check removed - users can proceed directly to address setup
  // Name and village will be collected during checkout if needed
  useEffect(() => {
    console.log('✅ AddressSetupPage: Profile completion disabled - proceeding to address setup');
  }, [userProfile, navigate]);

  // Debug userProfile when component mounts
  useEffect(() => {
    console.log('🔍 AddressSetupPage: Current userProfile:', userProfile);
    console.log('🔍 AddressSetupPage: Has valid address?', userProfile ? hasValidDeliveryAddress(userProfile) : false);
  }, [userProfile]);

  // Debug navigation on component mount
  useEffect(() => {
    console.log('📍 AddressSetupPage - Component mounted');
    console.log('📍 AddressSetupPage - Current pathname:', window.location.pathname);
    console.log('📍 AddressSetupPage - Current URL:', window.location.href);
    
    // Reset profile visit counter to avoid false positives in loop detection
    sessionStorage.setItem('completeProfileVisits', '0');
    
    return () => {
      console.log('📍 AddressSetupPage unmounting - navigating away');
    };
  }, []);

  // Add error handling for profile/address issues
  useEffect(() => {
    const handleAddressError = async () => {
      try {
        console.log('🔍 Checking for address errors in profile:', userProfile);
        
        if (!userProfile) {
          console.log('❌ No userProfile available yet');
          return;
        }
        
        if (userProfile.deliveryAddress) {
          console.log('🔍 Current deliveryAddress structure:', JSON.stringify(userProfile.deliveryAddress, null, 2));
          
          // Check for invalid address structure
          const hasInvalidFormat = typeof userProfile.deliveryAddress === 'object' && 
            !userProfile.deliveryAddress.coordinates &&
            !userProfile.deliveryAddress.lat &&
            !userProfile.deliveryAddress.latitude;
            
          if (hasInvalidFormat) {
            console.error('❌ Found invalid address format in profile, will reset');
            // We could try to repair the profile here
          }
        } else {
          console.log('ℹ️ No deliveryAddress in profile yet');
        }
      } catch (error) {
        console.error('Error handling address validation:', error);
        // Log the full error for debugging
        console.error('Full error:', error);
      }
    };
    
    handleAddressError();
  }, [userProfile]);

  const handleMapSelection = (address) => {
    setSelectedAddress(address);
    setStep('details');
  };

  const handleAddressDetails = async (details) => {
    try {
      const addressId = Date.now().toString();
      const fullAddress = {
        id: addressId,
        ...selectedAddress,
        ...details,
        createdAt: new Date().toISOString(),
      };

      // Get current user document
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let currentAddresses = [];
      if (userDoc.exists() && userDoc.data().addresses) {
        currentAddresses = userDoc.data().addresses;
      }

      // Add new address to the array
      const updatedAddresses = [...currentAddresses, fullAddress];

      // Update user document with new addresses array
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          addresses: updatedAddresses
        });
      } else {
        await setDoc(userDocRef, {
          addresses: updatedAddresses,
          email: user.email,
          uid: user.uid
        });
      }
      
      // Also save as the first delivery address to the user profile
      const addressString = formatAddressString(fullAddress);
      
      // You would call your existing user profile update here
      setAddressSelected(true);
      handleAddressSave({ address: addressString, isAddressValid: true });
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  const formatAddressString = (address) => {
    let addressParts = [];
    if (address.houseNumber) addressParts.push(address.houseNumber);
    if (address.building) addressParts.push(address.building);
    addressParts.push(address.formattedAddress);
    if (address.landmark) addressParts.push(`Near ${address.landmark}`);
    return addressParts.join(', ');
  };

  const skipAddressSetup = () => {
    const homeRoute = getUserHomeRoute(userType);
    navigate(homeRoute, { replace: true });
  };

  const handleAddressSave = async (addressData) => {
    console.log('✅ AddressSetupPage: handleAddressSave called with:', addressData);
    console.log('🔄 Current userType:', userType);
    setAddressSelected(true);
    setIsRedirecting(true);
    
    try {
      // Check if the address data includes validation result from AddressSelector component
      const isPrevalidated = addressData?.isAddressValid === true;
      console.log(`🔍 Address pre-validated by AddressSelector: ${isPrevalidated ? 'Yes ✅' : 'No, needs validation'}`);
      
      // Wait to ensure profile has been refreshed with the new address data
      // Add a small delay to ensure Firestore has fully processed the update
      await new Promise(resolve => setTimeout(resolve, 500));
      const userProfileAfterSave = await refreshUserProfile();
      
      console.log('🔍 User profile after refresh:', userProfileAfterSave);
      console.log('🔍 DeliveryAddress property:', userProfileAfterSave?.deliveryAddress);
      
      // Log address details for debugging
      if (userProfileAfterSave?.deliveryAddress?.coordinates) {
        console.log('🔍 Coordinates from saved address:', userProfileAfterSave.deliveryAddress.coordinates);
      }
      
      // Check if we have the addressCompleted flag
      console.log('🔍 Address completed flag:', userProfileAfterSave?.addressCompleted);
      
      // Verify address data integrity with hasValidDeliveryAddress
      const addressIsValid = hasValidDeliveryAddress(userProfileAfterSave);
      console.log('✅ Address valid after save check:', addressIsValid);
      
      // If address is still not valid, show an error and don't redirect
      if (!addressIsValid) {
        console.error('❌ Address validation failed after save');
        alert('There was an issue saving your address. Please try again.');
        setIsRedirecting(false);
        return;
      }
      
      // Navigate to home page
      console.log('🏠 Redirecting to home page...');
      const homeRoute = getUserHomeRoute(userType);
      console.log('🔄 Navigating to:', homeRoute);
      
      // Add a success message
      alert(`Address saved successfully! Redirecting to your ${userType === 'admin' ? 'admin dashboard' : userType === 'delivery' ? 'delivery dashboard' : 'home page'}...`);
      
      // Navigate directly to home route with replace:true to avoid browser history issues
      navigate(homeRoute, { replace: true });
      
      // Force page reload as fallback in case React Router navigation doesn't work
      // Use a longer timeout to allow for the alert to be acknowledged
      setTimeout(() => {
        if (window.location.pathname === '/address-setup') {
          console.log('🔄 Fallback: Force reload to ensure redirect');
          window.location.href = homeRoute;
        }
      }, 2000);
    } catch (error) {
      console.error('❌ Error during address save and redirect:', error);
      alert('There was an error saving your address. Please try again.');
      setIsRedirecting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: 'var(--ff-primary-green)',
        padding: '20px 20px 120px 20px', // Add bottom padding for navigation
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <div style={{
        maxWidth: '800px',
        width: '100%',
        background: 'var(--ff-card-bg)',
        borderRadius: '16px',
        boxShadow: '0 10px 30px var(--ff-shadow-dark)',
        overflow: 'hidden',
        marginBottom: '100px' // Add space above bottom navigation
      }}>
        {/* Welcome Header */}
        <div style={{
          padding: '30px',
          background: 'var(--ff-primary-green)',
          color: 'var(--ff-text-white)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
          <h1 style={{ margin: '0 0 12px 0', fontSize: '28px' }}>
            Welcome to ForFeeder!
          </h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '16px' }}>
            Hi {user?.name || 'there'}! Let's set up your delivery address for a better experience.
          </p>
        </div>

        {/* User Type Info */}
        <div style={{
          padding: '20px',
          background: 'var(--background-color, #F8F9FA)',
          borderBottom: '1px solid var(--border-color, #C8E6C9)',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-block',
            background: userType === 'admin' ? 'var(--ff-admin-color, #f44336)' : 
                       userType === 'delivery' ? 'var(--ff-warning-color, #ff9800)' : 'var(--ff-primary-color, #4CAF50)',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {userType === 'admin' ? '👑 Admin Account' :
             userType === 'delivery' ? '🚚 Delivery Person' :
             '👤 Customer Account'}
          </div>
          <p style={{ margin: '12px 0 0 0', color: 'var(--ff-text-secondary, #666)', fontSize: '14px' }}>
            {userType === 'admin' ? 'You have full administrative access to the system.' :
             userType === 'delivery' ? 'You can track and manage delivery orders assigned to you.' :
             'Set your delivery address to receive orders at your preferred location.'}
          </p>
        </div>

        {/* Address Setup */}
        <div className="address-setup-container" style={{ paddingBottom: '120px' }}>
          {step === 'map' ? (
            <div className="map-step">
              <div className="step-header">
                <h5>Select Your Location</h5>
                <p className="text-muted">Drag the pin to mark your exact delivery location</p>
              </div>
              <div style={{ marginBottom: '30px' }}>
                <AddressPickerMap onAddressSelect={handleMapSelection} />
              </div>
            </div>
          ) : (
            <div className="details-step">
              <div className="step-header">
                <h5>Complete Address Details</h5>
                <p className="text-muted">Add details for accurate delivery</p>
              </div>
              
              <div className="selected-location mb-3">
                <div className="location-preview">
                  <i className="bi bi-geo-alt-fill text-primary me-2"></i>
                  <span>{selectedAddress?.formattedAddress}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-auto"
                    onClick={() => setStep('map')}
                  >
                    Change Location
                  </button>
                </div>
              </div>

              <AddressDetailsForm
                onSubmit={handleAddressDetails}
                onCancel={skipAddressSetup}
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {step === 'map' && (
            <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={skipAddressSetup}
              >
                ⏭️ Skip for Now
              </button>
              
              <div className="status-message">
                {isRedirecting ? (
                  <div className="alert alert-info mb-0">
                    🎉 Address Saved! Redirecting to home page...
                  </div>
                ) : (
                  <small className="text-muted">
                    📍 Click on the map or search for your location
                  </small>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Benefits Section */}
        <div style={{
          padding: '20px',
         
          }}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎯</div>
              <h4 style={{ margin: '0 0 8px 0', color: 'var(--ff-text-primary, #333)' }}>Accurate Delivery</h4>
              <p style={{ margin: 0, color: 'var(--ff-text-secondary, #666)', fontSize: '14px' }}>
                Get your orders delivered to the exact location
              </p>
            </div>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚡</div>
              <h4 style={{ margin: '0 0 8px 0', color: 'var(--ff-text-primary, #333)' }}>Faster Service</h4>
              <p style={{ margin: 0, color: 'var(--ff-text-secondary, #666)', fontSize: '14px' }}>
                Quick checkout without entering address every time
              </p>
            </div>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📱</div>
              <h4 style={{ margin: '0 0 8px 0', color: 'var(--ff-text-primary, #333)' }}>Real-time Tracking</h4>
              <p style={{ margin: 0, color: 'var(--ff-text-secondary, #666)', fontSize: '14px' }}>
                Track your delivery person's location in real-time
              </p>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default AddressSetupPage;
